import React, { useEffect, useRef, useState, useMemo } from "react";
import DashboardFilterSelector from '../components/dashboardFilterSelector';
import DynamicChart from '../components/charts/dynamicChart';
import SummaryTable from '../components/sections/summaryTable';
import FiltersSection from '../components/sections/filters';
import MainCards from '../components/sections/mainCards';
import ThdMainCards from '../components/sections/thd/mainCards';
import { useDispatch, useSelector } from "react-redux";
import { changeDimension, changeFilter, changeDashboardFilters, changePublisherView, getData} from '../stateManagement/actions/report';
import Loader from '../components/layout/loader';
import { chartPreparation } from "../services/dataHandling";
import { useAuth } from "../hooks/useAuth";
import { ADMIN_ACCESS, ADMIN_ACCESS_MASK, PARTITION_MAIN, PARTITION_THD} from "../utils/constants";
import { useLocation } from "react-router-dom";

const EMPTY_RULES = [];

const dimensions_main = [
    { label: 'Platform', value: 'provider', key: '1' },
    { label: 'Publisher', value: 'publisher', key: '2' },
    { label: 'Website', value: 'website', key: '3' },
    { label: 'Placement', value: 'placementFormat', key: '4' },
    { label: 'Media type', value: 'mediaType', key: '5' },
    { label: 'Auction type', value: 'auctionType', key: '6' },
];

const dimensions_thd = [
    { label: 'Channel', value: 'label', key: '1' },
    { label: 'Platform', value: 'provider', key: '2' },
    { label: 'Publisher', value: 'publisher', key: '3' },
    { label: 'Website', value: 'website', key: '4' },
    { label: 'Line item type', value: 'lineItemType', key: '5' },
    { label: 'Placement', value: 'placement', key: '6' },

];

const dimensions_thd_limited = [
    { label: 'Channel', value: 'label', key: '1' },
    { label: 'Website', value: 'website', key: '2' },
];

const DashboardPage = () => {
    const dispatch = useDispatch();

    const [fullReport, setFullReport] = useState([]);
    const [cardMetrics, setCardMetrics] = useState(null);
    const [chartData, setChartData] = useState({ topValues: [], data: [] });
    const [dataReloaded, setDataReloaded] = useState(false);
    const [selectedCard, setSelectedCard] = useState('revenue');

    /**
     * Tinem minte daca pagina curenta a fost deja initializata.
     * Astfel resetam dimension/filterBy doar la intrarea initiala in pagina,
     * nu la fiecare schimbare facuta ulterior de user.
     */
    const initializedPageRef = useRef(null);

    // Keep results tied to the request that produced them.
    const [loadedQueryKey, setLoadedQueryKey] = useState(null);

    const { user } = useAuth();

    const reportData = useSelector((state) => state.reportReducer);
    const {loading, period, dimension, filterBy, publisherView, dashboardFilters} = reportData;

    const location = useLocation();

    const isThdPage = location.pathname.includes(PARTITION_THD);
    const PARTITION = isThdPage ? PARTITION_THD : PARTITION_MAIN;

    const DIMENSIONS = isThdPage
        ? publisherView !== ADMIN_ACCESS_MASK
            ? dimensions_thd_limited
            : dimensions_thd
        : dimensions_main;

    const SelectedMainCards = isThdPage ? ThdMainCards : MainCards;

    const DEFAULT_DIMENSION = isThdPage ? 'label' : 'provider';
    const filterScope = JSON.stringify([user?._id, user?.access, user?.subAccount, publisherView, PARTITION]);
    const rules = dashboardFilters.scope === filterScope ? dashboardFilters.rules : EMPTY_RULES;
    const serializedRules = useMemo(() => JSON.stringify(rules), [rules]);
    const queryKey = JSON.stringify({period, dimension, filterBy, publisherView, partition: PARTITION, dashboardFilters: serializedRules});
    const visibleReport = loadedQueryKey === queryKey ? fullReport : EMPTY_RULES;

    useEffect(() => {
        // Returning to the page or changing account/partition starts with no rules.
        dispatch(changeDashboardFilters(filterScope, []));
        dispatch(changeFilter(''));
    }, [dispatch, filterScope]);

    const GREETINGS_FOR =
        publisherView === ADMIN_ACCESS_MASK
            ? user && user.subAccount
                ? 'ThematicDigital'
                : 'Gemius'
            : publisherView;

    /**
     * Setam publisherView la prima incarcare, daca nu exista deja in Redux.
     */
    useEffect(() => {
        if (user && user.access && !publisherView) {
            const accessList = user.access.split('+');
            dispatch(
                changePublisherView(
                    accessList[0] === ADMIN_ACCESS ? 'Gemius' : accessList[0]
                )
            );
        }
    }, [dispatch, user, publisherView]);

    /**
     * Reset + fetch controlat.
     * Problema initiala:
     * - intrai in pagina cu dimension ramas din alta pagina
     * - getData pleca o data cu dimension vechi
     * - apoi changeDimension(DEFAULT_DIMENSION) actualiza Redux
     * - getData pleca a doua oara cu dimension corect
     * Solutia:
     * - verificam daca trebuie resetat dimension/filterBy
     * - daca da, facem reset si iesim din efect
     * - request-ul pleaca abia la urmatorul render, cand Redux are valorile corecte
     */
    useEffect(() => {
        if (!publisherView) return;

        const pageKey = `${PARTITION}-${DEFAULT_DIMENSION}`;
        const isFirstRunForPage = initializedPageRef.current !== pageKey;

        const dimensionIsAllowed = DIMENSIONS.some(
            (item) => item.value === dimension
        );

        const needsDimensionReset =
            !dimensionIsAllowed ||
            (isFirstRunForPage && dimension !== DEFAULT_DIMENSION);

        const needsFilterReset =
            isFirstRunForPage && filterBy !== '';

        if (needsFilterReset) {
            dispatch(changeFilter(''));
        }

        if (needsDimensionReset) {
            dispatch(changeDimension(DEFAULT_DIMENSION));
        }

        /**
         * Foarte important:
         * daca am facut reset, NU facem getData in acelasi ciclu.
         * Asteptam urmatorul render, cu valorile corecte din Redux.
         */
        if (needsFilterReset || needsDimensionReset) {
            return;
        }

        initializedPageRef.current = pageKey;

        let active = true;
        setDataReloaded(false);
        dispatch(getData(
            {period, dimension, filterBy, publisherView, partition: PARTITION, dashboardFilters: serializedRules},
            (report, metrics) => {if (active) {setCardMetrics(metrics); setFullReport(report); setLoadedQueryKey(queryKey);}},
            reloaded => {if (active) setDataReloaded(reloaded);}
        ));
        return () => {active = false;};
    }, [dispatch, period, dimension, filterBy, publisherView, PARTITION, DEFAULT_DIMENSION, DIMENSIONS, serializedRules, queryKey]);

    /**
     * Pregatim datele pentru chart dupa ce API-ul a terminat reload-ul.
     */
    useEffect(() => {
        if (dataReloaded) {
            const computedData = chartPreparation(fullReport, dimension, selectedCard, period);
            setChartData(computedData);
        }
        // Intentionat nu includem dimension/period aici ca trigger principal,
        // pentru ca ele declanseaza deja fetch-ul. Chart-ul se reface cand vine fullReport nou.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fullReport, dataReloaded, selectedCard]);

    if (loading) {
        return <Loader />;
    }

    return (
        <>
            <h2>{`Dashboard for ${GREETINGS_FOR} account`}</h2>
            <FiltersSection key={filterScope}
                dimensions={DIMENSIONS}
                defaultSelection={DEFAULT_DIMENSION}
                filterControl={<DashboardFilterSelector key={filterScope} rules={rules}
                    onApply={nextRules => dispatch(changeDashboardFilters(filterScope, nextRules))}
                    publisherView={publisherView} partition={PARTITION}
                    hideProviders={!!user?.subAccount && user?.access !== ADMIN_ACCESS} />}
            />
            <div className="main-section">
                <SelectedMainCards
                    fullData={visibleReport}
                    cardMetrics={loadedQueryKey === queryKey ? cardMetrics : null}
                    dashboardFilters={serializedRules}
                    partition={PARTITION}
                    trigger={setSelectedCard}
                    currentState={selectedCard}
                />
                <DynamicChart
                    fullData={loadedQueryKey === queryKey ? chartData : {topValues: [], data: []}}
                    currentState={selectedCard}
                />
            </div>
            <SummaryTable
                fullData={visibleReport}
                dashboardFilters={serializedRules}
                partition={PARTITION}
                currentState={selectedCard}
            />
        </>
    );
};

export default DashboardPage;